// src/auth.js
// Single entry point for authentication. In test mode a fixed test user replaces Firebase,
// so no Google popup is needed.
import {
    GoogleAuthProvider,
    EmailAuthProvider,
    OAuthProvider,
    createUserWithEmailAndPassword,
    deleteUser,
    onAuthStateChanged,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    revokeAccessToken,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithCredential,
    signInWithPopup,
    signOut,
    updatePassword,
    updateProfile,
    verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { auth } from './firebase';
import { isTestMode, TEST_USER } from './testMode/testMode';
import { isNativeApp } from './native';
import { diag } from './diagnostics';
import { endGuestSession, guestActive, guestUser, subscribeToGuest } from './guest';

const TEST_SIGNED_IN_KEY = 'tournamental.testUserSignedIn';
const testListeners = new Set();

// `tournamental.testUserName` (set by hand, e.g. in an automated check that needs several people) names another test user.
function currentTestUser() {
    try {
        if (window.localStorage.getItem(TEST_SIGNED_IN_KEY) !== '1') return null;
        const name = window.localStorage.getItem('tournamental.testUserName');
        return name ? { uid: name, displayName: name } : TEST_USER;
    } catch (error) {
        return null;
    }
}

function setTestUserSignedIn(signedIn) {
    try {
        if (signedIn) {
            window.localStorage.setItem(TEST_SIGNED_IN_KEY, '1');
        } else {
            window.localStorage.removeItem(TEST_SIGNED_IN_KEY);
        }
    } catch (error) {
        // Not persisted; the listeners below are still notified.
    }
    testListeners.forEach((listener) => listener(signedIn ? TEST_USER : null));
}

const FIREBASE_WAIT_MS = 5000;

// Calls callback with the current user (or null) now and on every change. Returns an unsubscribe function.
export function subscribeToAuth(callback) {
    if (isTestMode()) {
        testListeners.add(callback);
        callback(currentTestUser());
        return () => {
            testListeners.delete(callback);
        };
    }
    // Signed in with Google, or else the guest of this device (if that is who is using the app), or nobody. Somebody who
    // registered with an email and password counts as nobody until the address is confirmed (the server refuses them too).
    let firebaseUser;
    const emit = () => {
        if (firebaseUser === undefined) return; // Firebase has not said yet
        const waiting = needsVerification(firebaseUser);
        setPendingVerification(waiting ? firebaseUser : null);
        callback((!waiting && firebaseUser) || (guestActive() ? guestUser() : null));
    };
    const refresh = () => {
        firebaseUser = auth.currentUser || null;
        emit();
    };
    const stopFirebase = onAuthStateChanged(auth, (user) => {
        firebaseUser = user || null;
        emit();
    });
    // If Firebase does not answer at all, do not keep the app waiting: carry on as signed out (a late answer still counts).
    const waiting = setTimeout(() => {
        if (firebaseUser === undefined) {
            diag('warn', 'Firebase has not said who is signed in after 5 seconds: carrying on as signed out');
            firebaseUser = null;
            emit();
        }
    }, FIREBASE_WAIT_MS);
    const stopGuest = subscribeToGuest(emit);
    refreshers.add(refresh);
    return () => {
        clearTimeout(waiting);
        stopFirebase();
        stopGuest();
        refreshers.delete(refresh);
    };
}

// ---- confirming an email address ------------------------------------------------------------------------------
const refreshers = new Set();
const pendingListeners = new Set();
let pendingVerification = null;

function setPendingVerification(user) {
    if (user === pendingVerification) return;
    pendingVerification = user;
    pendingListeners.forEach((listener) => listener(user));
}

// Registered with an email and a password, and the address is not confirmed yet.
export const needsVerification = (user) =>
    Boolean(user) &&
    !user.emailVerified &&
    Array.isArray(user.providerData) &&
    user.providerData.length > 0 &&
    user.providerData[0].providerId === 'password';

// Calls `callback` with the Firebase user who still has to confirm their address (or null), now and on every change.
export function subscribeToPendingVerification(callback) {
    pendingListeners.add(callback);
    callback(pendingVerification);
    return () => pendingListeners.delete(callback);
}

// After the person says they followed the link: look again, and carry on if it is confirmed. Resolves whether it is.
export async function refreshVerification() {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true); // (the server reads the confirmation from the token)
    refreshers.forEach((refresh) => refresh());
    return Boolean(auth.currentUser.emailVerified);
}

export const resendVerification = () => sendEmailVerification(auth.currentUser);

// Headers that identify the signed-in user to the backend. The test user has no Firebase token,
// so in test mode the backend must run with AUTH_REQUIRED off (the default locally).
export async function getAuthHeaders() {
    if (isTestMode() || !auth.currentUser) return {};
    return { Authorization: `Bearer ${await auth.currentUser.getIdToken()}` };
}

// Inside the Android/iOS app a pop-up window does not work: the phone's own Google sign-in gives an ID token, which is
// handed to Firebase (the plugin is set to skip its native Firebase sign-in: see capacitor.config.json).
// A phone sign-in that never answers must not leave the app stuck: after this long it is given up.
const NATIVE_SIGN_IN_LIMIT_MS = 120000;
const withinTime = (promise, what) =>
    Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(() => reject(Object.assign(new Error(`${what} did not answer`), { code: 'auth/native-timeout' })), NATIVE_SIGN_IN_LIMIT_MS)
        ),
    ]);

async function nativeGoogleCredential() {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    const result = await withinTime(FirebaseAuthentication.signInWithGoogle(), 'Google sign-in');
    const idToken = result && result.credential && result.credential.idToken;
    if (!idToken) throw Object.assign(new Error('Google sign-in did not give an ID token'), { code: 'auth/popup-closed-by-user' });
    return GoogleAuthProvider.credential(idToken);
}

// Sign in with Apple, everywhere. Inside the apps the phone's own flow gives an ID token and a nonce for Firebase, and an
// authorization code that is needed to revoke the sign-in when the account is deleted; on the web a pop-up does the same
// through Firebase (it needs the Apple "Services ID" set up in Firebase, see README).
let appleAuthorizationCode = null;

const appleProvider = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
};

async function nativeAppleCredential() {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    const result = await withinTime(FirebaseAuthentication.signInWithApple(), 'Apple sign-in');
    const credential = result && result.credential;
    if (!credential || !credential.idToken) {
        throw Object.assign(new Error('Apple sign-in did not give an ID token'), { code: 'auth/popup-closed-by-user' });
    }
    appleAuthorizationCode = credential.authorizationCode || credential.accessToken || null;
    return new OAuthProvider('apple.com').credential({ idToken: credential.idToken, rawNonce: credential.nonce });
}

// What Firebase keeps of a web pop-up sign-in: the token that lets Apple revoke it later.
const rememberAppleToken = (result) => {
    const credential = OAuthProvider.credentialFromResult ? OAuthProvider.credentialFromResult(result) : null;
    appleAuthorizationCode = (credential && credential.accessToken) || null;
};

export const canSignInWithApple = () => true;

export async function signInWithApple() {
    diag('info', `sign-in: Apple started (${isNativeApp() ? 'phone' : 'browser'})`);
    let result;
    if (isNativeApp()) {
        result = await signInWithCredential(auth, await nativeAppleCredential());
    } else {
        result = await signInWithPopup(auth, appleProvider());
        rememberAppleToken(result);
    }
    endGuestSession();
    return result;
}

export async function signInWithGoogle() {
    diag('info', `sign-in: Google started (${isNativeApp() ? 'phone' : 'browser'})`);
    let result;
    if (isNativeApp()) {
        result = await signInWithCredential(auth, await nativeGoogleCredential());
    } else {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' }); // always ask which account, never pick one silently
        result = await signInWithPopup(auth, provider);
    }
    endGuestSession(); // from now on it is the account (what the guest made waits to be moved into it)
    return result;
}

// What to call somebody: their name, or else the first part of their email address.
export const displayNameOf = (user) => (user && (user.displayName || (user.email ? user.email.split('@')[0] : ''))) || '';

export async function signInWithEmail(email, password) {
    diag('info', 'sign-in: email started');
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    endGuestSession();
    return result;
}

// A new account with an email address and a password; it is signed in straight away.
export async function registerWithEmail(email, password) {
    const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
    try {
        await updateProfile(result.user, { displayName: email.trim().split('@')[0] });
    } catch (error) {
        // not important: displayNameOf falls back to the address
    }
    try {
        await sendEmailVerification(result.user); // the address must be confirmed before the account can be used
    } catch (error) {
        // the person can ask for it again on the next screen
    }
    endGuestSession();
    return result;
}

export const requestPasswordReset = (email) => sendPasswordResetEmail(auth, email.trim());

export const signInAsTestUser = () => setTestUserSignedIn(true);

export async function signOutUser() {
    if (isTestMode()) {
        setTestUserSignedIn(false);
    } else if (!auth.currentUser && guestActive()) {
        endGuestSession(); // a guest "signs out" of the device's guest session; their tournaments stay
    } else {
        await signOut(auth);
    }
}

// ---- deleting the account ---------------------------------------------------------------------------------------------
export const usesApple = (user) => Boolean(user && Array.isArray(user.providerData) && user.providerData[0]?.providerId === 'apple.com');

export const usesPassword = (user) => Boolean(user && Array.isArray(user.providerData) && user.providerData[0]?.providerId === 'password');

// Firebase wants a recent sign-in for something as final as this: ask for the password again, or for Google's account choice.
export async function confirmItIsYou(password) {
    const user = auth.currentUser;
    if (usesPassword(user)) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password || ''));
    } else if (usesApple(user)) {
        if (isNativeApp()) {
            await reauthenticateWithCredential(user, await nativeAppleCredential());
        } else {
            rememberAppleToken(await reauthenticateWithPopup(user, appleProvider()));
        }
    } else if (isNativeApp()) {
        await reauthenticateWithCredential(user, await nativeGoogleCredential());
    } else {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await reauthenticateWithPopup(user, provider);
    }
}

// Removes the sign-in itself (and with it the session).
export async function removeSignIn() {
    if (appleAuthorizationCode) {
        // Apple wants the sign-in revoked with Apple too when an account is deleted
        try {
            await revokeAccessToken(auth, appleAuthorizationCode);
        } catch (error) {
            console.error('Could not revoke the Apple sign-in:', error);
        }
        appleAuthorizationCode = null;
    }
    await deleteUser(auth.currentUser);
}

// ---- looking after the account ----------------------------------------------------------------------------------------
const profileListeners = new Set();

// Called when the name (or anything else shown about the person) changed: Firebase changes the user in place, so
// whoever shows it only has to draw it again.
export function subscribeToProfileChanges(listener) {
    profileListeners.add(listener);
    return () => profileListeners.delete(listener);
}

export async function changeDisplayName(name) {
    await updateProfile(auth.currentUser, { displayName: name.trim() });
    profileListeners.forEach((listener) => listener());
}

// How the person signs in, for showing: "Google", "Email and password".
export const signInMethodsOf = (user) =>
    (user && Array.isArray(user.providerData) ? user.providerData : [])
        .map((provider) => ({ 'google.com': 'Google', 'apple.com': 'Apple', password: 'Email and password' })[provider.providerId] || provider.providerId)
        .filter(Boolean);

// Both need a recent sign-in: the password is asked again.
export async function changePassword(currentPassword, newPassword) {
    await confirmItIsYou(currentPassword);
    await updatePassword(auth.currentUser, newPassword);
}

// The new address has to be confirmed (a link goes to it); the old one stays until then.
export async function requestEmailChange(currentPassword, newEmail) {
    await confirmItIsYou(currentPassword);
    await verifyBeforeUpdateEmail(auth.currentUser, newEmail.trim());
}
